import type { CSSProperties } from 'react';
import type { AvatarCatalog } from '../../api/avatar-catalog';
import { uiIn, uiText } from '../../i18n';
import { text } from './studio-content';
import { StudioIcon } from './StudioIcon';
import { FadeImg } from '../../ui/LateFade';
import type { ImgHTMLAttributes } from 'react';

/** Картинка кадра слайдера. Смена кадра снимает старый кадр на холст и рассыпает его частицами («щелчок Таноса», studio-hero-renderer):
    картинка с чужого адреса без разрешения CORS «пачкает» холст, частицы не читают его пикселей, и кадр менялся без них. Портреты персонажей
    и аватарки голосов приходят публичными адресами хранилища — грузим их с crossOrigin. Свой адрес (?cors=1): та же картинка в коллажах и
    списках грузится без CORS, и кэш браузера отдал бы её копию без разрешения — запрос с crossOrigin упал бы. blob: и свой сайт — как есть */
export function heroSrc(url: string) {
  if (!/^https?:/i.test(url) || url.startsWith(location.origin)) return url;
  return url + (url.includes('?') ? '&' : '?') + 'cors=1';
}
export function HeroImg({ src, ...props }: ImgHTMLAttributes<HTMLImageElement> & { src: string }) {
  const own = !/^https?:/i.test(src) || src.startsWith(location.origin);
  return <FadeImg {...props} src={heroSrc(src)} crossOrigin={own ? undefined : 'anonymous'} />;
}
import { artworkUrl, studioArtPhotos, studioOrb, studioOrbColors, studioWave } from './studio-art-model';
/** Есть ли чем занять правую колонку кадра: те же условия, по которым StudioHeroArt что-то рисует.
    Нужно главной: на живом стенде у кадров нет ни обложки, ни портретов, и пустая колонка 150px
    сжимала текст в треть ширины (#studio .st-hs — grid-template-columns: minmax(0,1fr) 150px). */
/** Персонажи ряда голосов: эталон берёт их у самого кадра (h.ch слайда «Озвучка» — [2104, 2101, 2106])
    и подписывает именем персонажа (cname), а не списком голосов аккаунта.
    Кружок строки — аватарка голоса (avatar_url основного голоса персонажа; у голосов это энсо бренда, решение владельца 28.10.2026),
    как в списке «Озвучки»; конический градиент эталона — только у голоса без картинки. */
function voicesOf(catalog?: AvatarCatalog, ids?: unknown) {
  const requested = Array.isArray(ids) ? ids : [];
  const faces = new Map((catalog?.default_voices ?? []).map(voice => [voice.id, artworkUrl(voice.avatar_thumb_url || voice.avatar_url)]));
  const cards = requested.map(id => catalog?.avatars.find(card => card.id === id)).filter(card => !!card);
  if (cards.length)
    return cards.slice(0, 3).map(card => {
      const lead = card.default_voice_id ?? card.voice_ids[0];
      return { id: card.id, name: card.name, face: (lead != null && faces.get(lead)) || null };
    });
  return (catalog?.default_voices ?? [])
    .slice(0, 3)
    .map(voice => ({ id: voice.id, name: text(voice.name), face: faces.get(voice.id) ?? null }));
}
/** Виды правой части кадра по контракту (docs/03-api.md, slides[].art) */
const ART_KINDS = new Set(['note', 'swap', 'fan', 'voices', 'clone']);
/** pending — картинки витрины ещё едут: у кадра со своим видом колонка стоит сразу, текст не перекладывается,
    когда портреты придут (у эталона всё синхронно, колонка есть с первого кадра) */
export function hasStudioHeroArt(kind: unknown, catalog?: AvatarCatalog, ids?: unknown, pending = false): boolean {
  if (pending && ART_KINDS.has(String(kind))) return true;
  if (kind === 'voices') return voicesOf(catalog, ids).length > 0;
  return studioArtPhotos(catalog, ids).length > 0;
}
/** pending — портреты витрины ещё едут: вид стоит своей формой, вместо картинок — плитки-заглушки (.sk, пульс скелетона);
    пришедший портрет проявляется поверх своей плитки (FadeImg и появление img в studio.css), плитки не пересоздаются */
export function StudioHeroArt({
  kind,
  catalog,
  ids,
  pending = false,
}: {
  kind: unknown;
  catalog?: AvatarCatalog;
  ids?: unknown;
  pending?: boolean;
}) {
  const photos = studioArtPhotos(catalog, ids);
  const voices = kind === 'voices' ? voicesOf(catalog, ids) : [];
  const pic = (url: string | undefined) => (url ? <HeroImg src={url} alt="" /> : <i className="sk" />);
  if (kind === 'voices' && !voices.length && pending)
    return (
      <span className="st-vl">
        {[0, 1, 2].map(i => (
          <span key={i} className="vr sk" />
        ))}
      </span>
    );
  if (voices.length)
    return (
      /* span, а не div: эталон (artHTML, случай voices) собирает ряд голосов строчными элементами */
      <span className="st-vl">
        {voices.map((voice, index) => {
          const [c1, c2] = studioOrbColors(voice.id);
          return (
            <span key={voice.id} className={`vr${index === 1 ? ' on' : ''}`}>
              {voice.face ? (
                <HeroImg className="av" src={voice.face} alt="" />
              ) : (
                /* голос без картинки — конический градиент; data-draw/data-c1/c2 нужны снимку кадра, который градиенты CSS не читает */
                <i data-draw="orb" data-c1={c1} data-c2={c2} style={{ background: studioOrb(voice.id) }} />
              )}
              <b>{voice.name}</b>
              <span className="st-wv" aria-hidden="true">
                {studioWave(7, voice.id % 13).map((bar, i) => (
                  <i key={i} style={{ height: bar.height }} />
                ))}
              </span>
            </span>
          );
        })}
      </span>
    );
  if (!photos.length && !pending) return null;
  if (kind === 'note')
    return (
      <span className="st-note">
        {/* кольцо рисует CSS (#studio .st-ring — conic-gradient с маской), элемент пустой, как в artHTML */}
        <span className="st-ring" data-draw="ring" data-p="62" />
        {pic(photos[0])}
        <span className="pb">
          <StudioIcon kind="play" />
        </span>
        {/* отметка времени кружка — в эталоне постоянная (artHTML, случай note) */}
        <em>0:14</em>
      </span>
    );
  if (kind === 'swap')
    return (
      <span className="st-swp">
        <span className={photos[0] ? 'card' : 'card sk'}>
          {photos[0] && <HeroImg src={photos[0]} alt="" />}
          {/* рамка ролика с отметкой времени, как в artHTML (случай swap) */}
          <span className="chip">
            <StudioIcon kind="play" />
            <em>0:12</em>
          </span>
        </span>
        {(photos[1] || pending) && <span className="me">{pic(photos[1])}</span>}
        <span className="to">
          <StudioIcon kind="chev" />
        </span>
      </span>
    );
  /* «Ваш голос»: образец (микрофон и волна) → стрелка вниз → карточка «Мой голос · Клон, готов»
     с вашим снимком и галочкой. Дословно artHTML, случай clone (engine.ts:416-418). */
  if (kind === 'clone')
    return (
      <span className="st-clone">
        <span className="smp">
          <span className="mi">
            <StudioIcon kind="mic" />
          </span>
          <span className="st-wv" aria-hidden="true">
            {studioWave(13, 7, 60).map((bar, index) => (
              <i key={index} style={{ height: bar.height, '--d': bar.delay } as CSSProperties} />
            ))}
          </span>
        </span>
        <span className="to">
          <StudioIcon kind="chevD" />
        </span>
        <span className="vc">
          {photos[0] ? <HeroImg src={photos[0]} alt="" draggable={false} /> : <i className="sk" />}
          <span className="m">
            <b>{uiIn('студия', 'Мой голос')}</b>
            <small>{uiText('Клон · готов')}</small>
          </span>
          <i className="ok">
            <StudioIcon kind="check" />
          </i>
        </span>
      </span>
    );
  return (
    <span className="st-fan">
      {(pending && !photos.length ? ['', '', ''] : photos.slice(0, 3)).map((url, i) => (
        <i key={i} data-draw="rot" className={url ? undefined : 'sk'}>
          {url && <HeroImg src={url} alt="" />}
        </i>
      ))}
    </span>
  );
}
